import { effect, inject, Injectable, signal, untracked, WritableSignal } from "@angular/core";
import { BooleanTristateValue, JsonReply, SelectOption, SelectOptionGroup } from "@grupo-constant/next-dtype/types";
import { compareAsc, endOfDay, format, parse, parseISO, startOfDay, startOfToday } from "date-fns";
import { firstValueFrom, map, of, tap } from "rxjs";
import { USER_URI } from "../projects/projects.service";
import { BeautifyWorkshiftEventReply, BeautifyWorkshiftEventRequest, CancelWorkshiftListReply, CancelWorkshiftListRequest, DeleteWorkshiftReply, DeleteWorkshiftRequest, FinishPeriodicEventRequest, FlushWorkshiftModelReply, FlushWorkshiftModelRequest, FlushWorkshiftReply, FlushWorkshiftRequest, ForceWorkshiftStartEndRequest, ResetWorkshiftListReply, ResetWorkshiftListRequest, RetrieveWorkplaceEventSummaryReply, RetrieveWorkplaceEventSummaryRequest, RetrieveWorkshiftByIdReply, RetrieveWorkshiftByIdRequest, RetrieveWorkshiftResourcesReply, RetrieveWorkshiftResourcesRequest, RetrieveWorkshiftSearchOptionsReply, RetrieveWorkshiftSearchOptionsRequest, RetrieveWorkshiftUserEventListReply, RetrieveWorkshiftUserEventListRequest, SearchWorkshiftElement, SearchWorkshiftModelElement, SearchWorkshiftModelReply, SearchWorkshiftModelRequest, SearchWorkshiftReply, SearchWorkshiftRequest, SendWorkshiftListReply, SendWorkshiftListRequest, WorkshiftPeriodicityStatusEnum, WorkshiftSearchOptionsFilterEnum, WorkshiftSearchOptionsReasonEnum } from "./workshifts.types";
import { HttpService } from "../../../core/http/http.service";
import { StorageKeysEnum } from "../../../core/enums/storage.keys.enum";
import { NextMethodsEnum } from "../../../core/enums/next-methods.enum";
import { WebSocketMessage, WebSocketTypeEnum, WebSocketWorkshiftRowUpdateMessage, WebSocketWorkshiftUserEventUpdateMessage } from "../../../core/websocket/websocket.types";

@Injectable({
    providedIn: 'root'
})
export class WorkshiftsService {

    public readonly chunkSize = 100;

    private http = inject(HttpService);

    private _workshift: WritableSignal<RetrieveWorkshiftByIdReply> = signal(undefined);
    readonly workshift = this._workshift.asReadonly();
    private _workshifts: WritableSignal<SearchWorkshiftElement[]> = signal([]);
    readonly workshifts = this._workshifts.asReadonly();
    private _workshiftUserEventUpdateMsg: WritableSignal<WebSocketWorkshiftUserEventUpdateMessage | undefined> = signal(undefined);
    readonly workshiftUserEventUpdateMsg = this._workshiftUserEventUpdateMsg.asReadonly();
    private _workshiftModel: WritableSignal<SearchWorkshiftModelElement> = signal(undefined);
    readonly workshiftModel = this._workshiftModel.asReadonly();
    private _workshiftModels: WritableSignal<SearchWorkshiftModelElement[]> = signal([]);
    readonly workshiftModels = this._workshiftModels.asReadonly();
    private _filters: WritableSignal<SearchWorkshiftRequest> = signal(undefined);
    readonly filters = this._filters.asReadonly();
    private _modelFilters: WritableSignal<SearchWorkshiftModelRequest> = signal(undefined);
    readonly modelFilters = this._modelFilters.asReadonly();
    private _count: WritableSignal<number> = signal(undefined);
    readonly count = this._count.asReadonly();
    private _modelCount: WritableSignal<number> = signal(undefined);
    readonly modelCount = this._modelCount.asReadonly();

    private _availableSearchOptions: WritableSignal<WorkshiftSearchOptionsFilterEnum[]> = signal([]);
    readonly availableSearchOptions = this._availableSearchOptions.asReadonly();
    private _taskType: WritableSignal<SelectOptionGroup[]> = signal([]);
    readonly taskType = this._taskType.asReadonly();
    private _workplaces: WritableSignal<SelectOption[]> = signal([]);
    readonly workplaces = this._workplaces.asReadonly();
    private _schedule: WritableSignal<{ from: string, to: string}> = signal(undefined);
    readonly schedule = this._schedule.asReadonly();
    private _statuses: WritableSignal<SelectOption[]> = signal([]);
    readonly statuses = this._statuses.asReadonly();
    private _reason: WritableSignal<SelectOption[]> = signal([]);
    readonly reason = this._reason.asReadonly();

    private _dateFrom = signal(startOfToday());
    readonly dateFrom = this._dateFrom.asReadonly();
    private _dateTo = signal(startOfToday());
    readonly dateTo = this._dateTo.asReadonly();
    private _viewMode: WritableSignal<'workshift' | 'model'> = signal('workshift');
    readonly viewMode = this._viewMode.asReadonly();

    constructor() {
        effect(() => {
            const filters = this._filters();
            const size = localStorage.getItem(StorageKeysEnum.workshiftPaginationSize);
            const paginationSize = size ? Number(size) : 100;
            if (filters) {
                untracked(async () => {
                    if (this._viewMode() === 'workshift') {
                        await firstValueFrom(this.getWorkshifts(paginationSize, 0, true));
                    }
                });
            }
        });

        effect(() => {
            const filters = this._modelFilters();
            const size = localStorage.getItem(StorageKeysEnum.workshiftModelPaginationSize);
            const paginationSize = size ? Number(size) : 100;
            if (filters) {
                untracked(async () => {
                    if (this._viewMode() === 'model') {
                        await firstValueFrom(this.getWorkshiftModels(paginationSize, 0, true));
                    }
                });
            }
        });
    }

    setWorkshift(workshift: RetrieveWorkshiftByIdReply) {
        this._workshift.set(workshift);
    }

    setWorkshiftUserEventUpdateMsg(message: WebSocketWorkshiftUserEventUpdateMessage) {
        message.summaryByWorkshift.userName = message.partaker[USER_URI]?.[message.userId]?.displayName;
        message.daySummary.userName = message.partaker[USER_URI]?.[message.userId]?.displayName;
        this._workshiftUserEventUpdateMsg.set(message);
    }

    setWorkshiftModel(model: SearchWorkshiftModelElement) {
        this._workshiftModel.set(model);
    }

    setFilters(filters: SearchWorkshiftRequest) {
        this._filters.set(filters);
    }

    setModelFilters(filters: SearchWorkshiftModelRequest) {
        this._modelFilters.set(filters);
    }

    setDateFrom(dateFrom: Date) {
        this._dateFrom.set(dateFrom);
    }

    setDateTo(dateTo: Date) {
        this._dateTo.set(dateTo);
    }

    setViewMode(mode: 'workshift' | 'model') {
        this._viewMode.set(mode);
    }

    retrieveWorkshiftResources(request: RetrieveWorkshiftResourcesRequest) {
        return this.http.post<RetrieveWorkshiftResourcesReply>(NextMethodsEnum.retrieveWorkshiftResources, request)
            .pipe(
                map(reply => {
                    return reply.workshiftResoruces?.map(resource => {
                        resource.userName = reply.partaker[USER_URI]?.[resource.userId]?.displayName;
                        return resource;
                    }) ?? [];
                })
            );
    }

    retrieveWorkshiftById(request: RetrieveWorkshiftByIdRequest) {
        return this.http.post<RetrieveWorkshiftByIdReply>(NextMethodsEnum.retrieveWorkshiftById, request)
            .pipe(
                map(reply => {
                    reply.row.who.resources = reply.row.who.resources?.map(resource => {
                        resource.userName = reply.partaker[USER_URI][resource.userId]?.displayName;
                        return resource;
                    }) ?? [];
                    return reply;
                })
            );
    }

    flushWorkshift(request: FlushWorkshiftRequest) {
        return this.http.post<FlushWorkshiftReply>(NextMethodsEnum.flushWorkshift, request);
    }

    flushWorkshiftModel(request: FlushWorkshiftModelRequest) {
        return this.http.post<FlushWorkshiftModelReply>(NextMethodsEnum.flushPeriodicEvent, request);
    }

    retrieveWorkshiftSearchOptions(request: RetrieveWorkshiftSearchOptionsRequest) {
        return this.http.post<RetrieveWorkshiftSearchOptionsReply>(NextMethodsEnum.retrieveWorkshiftSearchOptions, request).pipe(
            tap(reply => {
                this._taskType.set(reply.taskType);
                this._workplaces.set(reply.workplaces);
                this._schedule.set(reply.schedule);
                this._statuses.set(reply.statuses);
                this._reason.set(reply.reason);
                this._availableSearchOptions.set(reply.availableSearchOptions);
            })
        );
    }

    beautifyWorkshiftEvent(request: BeautifyWorkshiftEventRequest) {
        return this.http.post<BeautifyWorkshiftEventReply>(NextMethodsEnum.beautifyWorkshiftEvent, request).pipe(
            map(reply => {
                reply.eventSummary.userName = reply.partaker[USER_URI][reply.eventSummary.userId]?.displayName;
                return reply;
            })
        )
    }

    getWorkshifts(size: number, page: number = 0, refresh: boolean = false) {
        const workshifts = [...this._workshifts()];
        const filters = this._filters();
        if (refresh || !workshifts.length || (workshifts.length && filters.serviceId !== workshifts[0].workshift?.serviceId)) {
            filters.lastConsumedWorkshiftId = undefined;
            this.setFilters(filters);
            return this.searchWorkshifts(filters).pipe(
                map(reply => {
                    this._count.set(reply.count);
                    return reply.workshift ?? []
                }),
                tap(workshifts => this._workshifts.set(workshifts))
            );
        } else if (page * size >= workshifts.length) {
            filters.lastConsumedWorkshiftId = workshifts[workshifts.length - 1].workshift.workshiftId;
            this.setFilters(filters);
            return this.searchWorkshifts(filters).pipe(
                map(reply => {
                    this._count.set(reply.count);
                    const newWorkshifts = reply.workshift ?? [];
                    workshifts.push(...newWorkshifts);
                    return workshifts;
                }),
                tap(workshifts => this._workshifts.set(workshifts))
            );
        } else {
            this._workshifts.set(workshifts);
            return of(workshifts);
        }
    }

    getWorkshiftModels(size: number, page: number = 0, refresh: boolean = false) {
        const workshiftModels = [...this._workshiftModels()];
        const filters = this._modelFilters();
        if (refresh || !workshiftModels.length || (workshiftModels.length && filters.serviceId !== workshiftModels[0].workshift?.serviceId)) {
            filters.lastConsumedModelId = undefined;
            this.setModelFilters(filters);
            return this.searchWorkshiftModels(filters).pipe(
                map(reply => {
                    this._modelCount.set(reply.count);
                    return reply.searchPeriodicModelElement ?? []
                }),
                tap(models => this._workshiftModels.set(models))
            );
        } else if (page * size >= workshiftModels.length) {
            filters.lastConsumedModelId = workshiftModels[workshiftModels.length - 1].model.modelId;
            this.setModelFilters(filters);
            return this.searchWorkshiftModels(filters).pipe(
                map(reply => {
                    this._modelCount.set(reply.count);
                    const newModels = reply.searchPeriodicModelElement ?? [];
                    workshiftModels.push(...newModels);
                    return workshiftModels;
                }),
                tap(models => this._workshiftModels.set(models))
            );
        } else {
            this._workshiftModels.set(workshiftModels);
            return of(workshiftModels);
        }
    }

    handleWebSocketMessage(message: WebSocketMessage) {
        if (message.type === WebSocketTypeEnum.WORKSHIFT_ROW_UPDATE) {
            if (this.passFilter(message.payload)) {
                this.updateWorkshift(message.payload);
            } else {
                this.deleteWorkshift(message.payload);
            }
        }
    }

    addWorkshiftModel(model: SearchWorkshiftModelElement) {
        this._workshiftModels.update(models => {
            const index = models.findIndex(m => m.model.modelId === model.model.modelId);
            if (index > -1) {
                models.splice(index, 1);
            }
            models.unshift(model);
            return [...models];
        });
        this._modelCount.set(this._workshiftModels().length);
    }

    finishWorkshiftModel(model: SearchWorkshiftModelElement) {
        this._workshiftModels.update(models => {
            const index = models.findIndex(m => m.model.modelId === model.model.modelId);
            if (index > -1) {
                models.splice(index, 1, model);
            }
            return [...models];
        });
        this._modelCount.set(this._workshiftModels().length);
    }

    sendWorkshiftList(request: SendWorkshiftListRequest) {
        return this.http.post<SendWorkshiftListReply>(NextMethodsEnum.sendWorkshiftList, request);
    }

    cancelWorkshiftList(request: CancelWorkshiftListRequest) {
        return this.http.post<CancelWorkshiftListReply>(NextMethodsEnum.cancelWorkshiftList, request);
    }
    resetWorkshiftList(request: ResetWorkshiftListRequest) {
        return this.http.post<ResetWorkshiftListReply>(NextMethodsEnum.resetWorkshiftList, request);
    }

    startEndWorkshift(request: ForceWorkshiftStartEndRequest) {
        return this.http.post<JsonReply>(NextMethodsEnum.forceWorkshiftStartEnd, request);
    }

    deleteWorkshiftList(request: DeleteWorkshiftRequest) {
        return this.http.post<DeleteWorkshiftReply>(NextMethodsEnum.deleteWorkshiftList, request);
    }

    finishPeriodicEvent(request: FinishPeriodicEventRequest) {
        return this.http.post<FlushWorkshiftModelReply>(NextMethodsEnum.finishPeriodicEvent, request);
    }

    retrieveWorkplaceEventSummary(request: RetrieveWorkplaceEventSummaryRequest) {
        return this.http.post<RetrieveWorkplaceEventSummaryReply>(NextMethodsEnum.retrieveWorkplaceEventSummary, request);
    }

    retrieveWorkshiftUserEventList(request: RetrieveWorkshiftUserEventListRequest) {
        return this.http.post<RetrieveWorkshiftUserEventListReply>(NextMethodsEnum.retrieveWorkshiftUserEventList, request)
            .pipe(map(reply => reply.events ?? []));
    }

    private searchWorkshifts(request: SearchWorkshiftRequest) {
        return this.http.post<SearchWorkshiftReply>(NextMethodsEnum.searchWorkshift, request);
    }

    private searchWorkshiftModels(request: SearchWorkshiftModelRequest) {
        return this.http.post<SearchWorkshiftModelReply>(NextMethodsEnum.searchPeriodicModel, request)
            .pipe(map(reply => {
                for (const model of reply.searchPeriodicModelElement ?? []) {
                    for (const event of model.model.modelStatusEvents) {
                        event.userName = reply.partaker[USER_URI]?.[event.userId]?.displayName;
                    }
                }
                return reply;
            }));
    }
    
    private passFilter(payload: WebSocketWorkshiftRowUpdateMessage) {
        const workshift = payload.row;
        const filters = this._filters();
        if (!filters) {
            return false;
        }
        if (filters.serviceId !== workshift.workshift.serviceId) {
            return false;
        }
        if (this.workplaces().findIndex(workplace => workplace.code === workshift.group.workplaceCode) === -1 || (filters.workplaceCode.length && !filters.workplaceCode.includes(workshift.group.workplaceCode))) {
            return false;
        }
        if (filters.taskTypeId.length && !filters.taskTypeId.includes(workshift.workshift.taskTypeId)) {
            return false;
        }
        if (filters.handwrittenId.length && !filters.handwrittenId.includes(workshift.workshift.workshiftId) && !filters.handwrittenId.includes(workshift.workshift.externalWorkshiftId)) {
            return false;
        }
        if (filters.statuses.length && !filters.statuses.includes(workshift.status.status)) {
            return false;
        }
        let filterDateFrom: Date;
        if (filters.from) {
            filterDateFrom = startOfDay(parse(filters.from, 'yyyy-MM-dd', startOfToday()));
        }
        let filterDateTo: Date;
        if (filters.to) {
            filterDateTo = endOfDay(parse(filters.to, 'yyyy-MM-dd', startOfToday()));
        }
        const workshiftDateFrom = parse(workshift.schedule?.from, 'yyyy-MM-dd', startOfToday());
        if (filterDateFrom && filterDateTo) {
            if (compareAsc(workshiftDateFrom, filterDateFrom) < 0 || compareAsc(workshiftDateFrom, filterDateTo) > 0) {
                return false;
            }
        } else if (filterDateFrom) {
            if (compareAsc(workshiftDateFrom, filterDateFrom) < 0) {
                return false;
            }
        } else if (filterDateTo) {
            if (compareAsc(workshiftDateFrom, filterDateTo) > 0) {
                return false;
            }
        }
        const workshiftTimeFrom = format(parseISO(workshift.schedule.timeFrom), 'HH:mm:ss');
        if (filters.from_time && filters.to_time) {
            if (filters.from_time > workshiftTimeFrom || filters.to_time < workshiftTimeFrom) {
                return false;
            }
        } else if (filters.from_time) {
            if (filters.from_time > workshiftTimeFrom) {
                return false;
            }
        } else if (filters.to_time) {
            if (filters.to_time < workshiftTimeFrom) {
                return false;
            }
        }
        for (const reason of filters.reason) {
            switch(reason as WorkshiftSearchOptionsReasonEnum) {
                case WorkshiftSearchOptionsReasonEnum.DELAYED_START:
                    if (!workshift.schedule?.delayedStart) {
                        return false;
                    }
                    break;
                case WorkshiftSearchOptionsReasonEnum.DELAYED_END:
                    if (!workshift.schedule?.delayedEnd) {
                        return false;
                    }
                    break;
                case WorkshiftSearchOptionsReasonEnum.STARTED_WITHOUT_GEOPOSITION:
                    if (workshift.status?.startedWithoutGeoposition !== BooleanTristateValue.TRUE) {
                        return false;
                    }
                    break;
                case WorkshiftSearchOptionsReasonEnum.FINISHED_WITHOUT_GEOPOSITION:
                    if (workshift.status?.finishedWithoutGeoposition !== BooleanTristateValue.TRUE) {
                        return false;
                    }
                    break;
                case WorkshiftSearchOptionsReasonEnum.WITHOUT_ASSIGNED_RESOURCE:
                case WorkshiftSearchOptionsReasonEnum.WITHOUT_ASSIGNED_PERSON:
                    if (workshift.who?.resources.filter(r => r.assigned).length === 0) {
                        return false;
                    }
                    break;
                case WorkshiftSearchOptionsReasonEnum.WITHOUT_ASSIGNED_HORARY:
                    if (workshift.schedule?.timeFrom || workshift.schedule?.timeTo) {
                        return false;
                    }
                    break;
                default:
                    break;
            }
        }
        return true;
    }

    private updateWorkshift(payload: WebSocketWorkshiftRowUpdateMessage) {
        this._workshifts.update(workshifts => {
            const index = workshifts.findIndex(w => w.workshift.workshiftId === payload.row.workshift.workshiftId);
            if (index > -1) {
                workshifts.splice(index, 1);
            }
            workshifts.unshift(payload.row);
            return [...workshifts];
        });
        if (payload.row.workshift.workshiftId === this._workshift()?.row.workshift.workshiftId) {
            this._workshift.update(workshift => {
                workshift.row = payload.row;
                return { ...workshift };
            });
        }
        this._count.set(this._workshifts().length);
    }

    private deleteWorkshift(payload: WebSocketWorkshiftRowUpdateMessage) {
        this._workshifts.update(workshifts => {
            const index = workshifts.findIndex(w => w.workshift.workshiftId === payload.row.workshift.workshiftId);
            if (index > -1) {
                workshifts.splice(index, 1);
                return [...workshifts];
            }
            return workshifts;
        });
        this._count.set(this._workshifts().length);
    }
}